import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Button,
  useToast,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
} from '@chakra-ui/react';
import {
  Activity,
  AlertCircle,
  Check,
  ChevronRight,
  Clock3,
  Cog,
  Droplets,
  RefreshCw,
  RotateCcw,
  ShieldAlert,
} from 'lucide-react';
import { fetchPumpIds, fetchDashboard, reinitializePump } from './api';
import './prediction.css';
import { PumpHealthIndicators } from './components/PumpHealthIndicators';
import { Readings } from './components/Readings';
import { RootCauseDiagnostics } from './components/RootCauseDiagnostics';
import { TelemetryTable } from './components/TelemetryTable';
import { TelemetryCharts } from './components/TelemetryCharts';
import { DashboardSkeleton } from './components/DashboardSkeleton';
import { formatIst, isStale } from './format';
import { conditionLabel, conditionTone, statusMessage } from './status';

function PumpIdentity({ payload, pumpId, loading, error }) {
  const tone = payload ? conditionTone(payload) : 'unknown';
  const running = payload
    ? String(payload.running_status || payload.reported_pump_status || 'UNKNOWN').toUpperCase()
    : 'UNKNOWN';
  const online = running === 'ON';
  const StatusIcon =
    tone === 'normal'
      ? Check
      : tone === 'anomaly' || tone === 'error'
      ? ShieldAlert
      : tone === 'warning'
      ? AlertCircle
      : Clock3;

  return (
    <section className="pump-overview" aria-label="Selected pump overview">
      <div className="pump-summary">
        <div className="pump-visual" aria-hidden="true">
          <Droplets size={23} />
          <Cog className="pump-gear" size={42} />
        </div>
        <div className="pump-details">
          <p className="section-kicker">Selected asset</p>
          <h1>{pumpId || 'No pump selected'}</h1>
          <p>
            Transfer Pump <span>•</span> Plant A
          </p>
          <div className="pump-meta">
            <span className={`online-badge ${online ? 'is-online' : 'is-offline'}`}>
              <i />
              {online ? 'ONLINE' : running === 'UNKNOWN' ? 'UNKNOWN' : 'OFFLINE'}
            </span>
            <span>Last updated: {payload ? formatIst(payload.last_updated) : 'Unavailable'}</span>
          </div>
        </div>
      </div>

      <div className={`overall-status tone-${error ? 'error' : tone}`} role={error ? 'alert' : 'status'}>
        <div className="overall-status-icon">
          {loading && !payload ? (
            <RefreshCw className="spin" size={24} />
          ) : error ? (
            <AlertCircle size={24} />
          ) : (
            <StatusIcon size={24} />
          )}
        </div>
        <div>
          <span>Overall status</span>
          <strong>
            {loading && !payload
              ? 'Fetching fresh data'
              : error
              ? 'Data Unavailable'
              : payload
              ? conditionLabel(payload)
              : 'Waiting for selection'}
          </strong>
          <p>{error || (payload ? statusMessage(payload) : 'Choose a pump to begin monitoring.')}</p>
        </div>
      </div>
    </section>
  );
}

export default function Prediction() {
  const [pumpIds, setPumpIds] = useState([]);
  const [selectedPump, setSelectedPump] = useState('');
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [reinitializing, setReinitializing] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const cancelRef = useRef();
  const toast = useToast();

  const userRole = localStorage.getItem('role');
  const isAdmin = String(userRole || '').toLowerCase() === 'admin';

  const requestNumber = useRef(0);
  const activeRequest = useRef(null);
  const selectedRef = useRef(selectedPump);
  const loadingRef = useRef(false);

  useEffect(() => {
    selectedRef.current = selectedPump;
  }, [selectedPump]);

  const loadDashboard = useCallback(async (pumpId) => {
    if (!pumpId) return;
    const currentRequest = ++requestNumber.current;
    if (activeRequest.current) {
      activeRequest.current.abort();
    }
    const controller = new AbortController();
    activeRequest.current = controller;
    loadingRef.current = true;
    setLoading(true);
    setError(null);
    setPayload(null);

    try {
      const result = await fetchDashboard(pumpId, controller.signal);
      if (currentRequest !== requestNumber.current || pumpId !== selectedRef.current) return;
      setPayload({ ...result, pump_id: result.pump_id || pumpId });
    } catch (caught) {
      if (currentRequest !== requestNumber.current) return;
      setPayload(null);
      setError(caught instanceof Error ? caught.message : 'The PumpWatch API is unavailable.');
    } finally {
      if (currentRequest === requestNumber.current) {
        loadingRef.current = false;
        setLoading(false);
      }
    }
  }, []);

  // Fetch pump list on component mount
  useEffect(() => {
    const controller = new AbortController();
    fetchPumpIds(controller.signal)
      .then((ids) => {
        setPumpIds(ids);
        if (ids.length === 0) {
          setError('No pumps were returned by the PumpWatch API.');
          return;
        }
        const saved = window.localStorage.getItem('pumpwatch-selected-pump');
        const defaultId = saved && ids.includes(saved) ? saved : ids[0];
        setSelectedPump(defaultId);
      })
      .catch((caught) => {
        setError(caught instanceof Error ? caught.message : 'The PumpWatch API is unavailable.');
      });

    return () => controller.abort();
  }, []);

  // When selected pump changes, fetch dashboard
  useEffect(() => {
    if (!selectedPump) return;
    window.localStorage.setItem('pumpwatch-selected-pump', selectedPump);
    loadDashboard(selectedPump);

    return () => {
      if (activeRequest.current) {
        activeRequest.current.abort();
      }
      requestNumber.current += 1;
      loadingRef.current = false;
    };
  }, [loadDashboard, selectedPump]);

  const handleReinitialize = async () => {
    if (!selectedPump || reinitializing) return;
    setIsConfirmOpen(false);
    setReinitializing(true);

    try {
      await reinitializePump(selectedPump);
      toast({
        title: 'Pump Reinitialized',
        description: `Pump ${selectedPump} reinitialized successfully.`,
        status: 'success',
        duration: 4000,
        isClosable: true,
      });
      await loadDashboard(selectedPump);
    } catch (err) {
      toast({
        title: 'Reinitialization Failed',
        description: err.message || 'Failed to reinitialize pump.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setReinitializing(false);
    }
  };

  return (
    <Box pt={{ base: '130px', md: '80px', xl: '80px' }} pb="40px" className="prediction-page">
      <header className="topbar">
        <div className="breadcrumb-row">
          <div className="topbar-brand">
            <span>
              <Activity size={18} />
            </span>
            <strong>AquaSense</strong>
          </div>
          <span>Monitoring</span>
          <ChevronRight size={15} />
          <label className="pump-picker">
            <span className="sr-only" style={{ display: 'none' }}>
              Select pump
            </span>
            <select
              value={selectedPump}
              onChange={(event) => setSelectedPump(event.target.value)}
              disabled={pumpIds.length === 0 || loading}
            >
              {pumpIds.length === 0 && <option value="">No pumps available</option>}
              {pumpIds.map((id) => (
                <option key={id} value={id}>
                  {id}
                </option>
              ))}
            </select>
            <ChevronRight size={14} style={{ transform: 'rotate(90deg)' }} />
          </label>
        </div>
        <div className="topbar-actions">
          {isAdmin && (
            <button
              className="reinitialize-button"
              type="button"
              onClick={() => setIsConfirmOpen(true)}
              disabled={!selectedPump || loading || reinitializing}
              aria-label="Reinitialize pump"
              title="Reinitialize pump"
            >
              <RotateCcw size={15} className={reinitializing ? 'spin' : ''} />
              <span>Reinitialize</span>
            </button>
          )}
          <button
            className="icon-button"
            type="button"
            onClick={() => void loadDashboard(selectedPump)}
            disabled={!selectedPump || loading || reinitializing}
            aria-label="Refresh current pump data"
            title="Refresh current pump data"
          >
            <RefreshCw size={18} className={loading ? 'spin' : ''} />
          </button>
        </div>
      </header>

      <main className="dashboard-main">
        <PumpIdentity payload={payload} pumpId={selectedPump} loading={loading} error={error} />

        {loading && !payload ? (
          <DashboardSkeleton />
        ) : payload ? (
          <>
            {isStale(payload) && (
              <div className="stale-banner" role="status">
                <Clock3 size={17} />
                <span>This running pump's latest telemetry is stale.</span>
              </div>
            )}

            <Readings payload={payload} />

            <div className="dashboard-primary-grid">
              <PumpHealthIndicators payload={payload} />
              <div className="card-panel chart-panel" id="trend-panel">
                <TelemetryCharts payload={payload} />
              </div>
            </div>

            <RootCauseDiagnostics payload={payload} />

            <TelemetryTable payload={payload} />
          </>
        ) : null}
      </main>

      {isAdmin && (
        <AlertDialog
          isOpen={isConfirmOpen}
          leastDestructiveRef={cancelRef}
          onClose={() => !reinitializing && setIsConfirmOpen(false)}
          isCentered
        >
          <AlertDialogOverlay>
            <AlertDialogContent mx={4} borderRadius="16px">
              <AlertDialogHeader fontSize="lg" fontWeight="bold">
                Reinitialize Pump
              </AlertDialogHeader>
              <AlertDialogBody>
                Are you sure you want to reinitialize <strong>{selectedPump}</strong>? This
                will trigger administrative reinitialization for this pump.
              </AlertDialogBody>
              <AlertDialogFooter>
                <Button
                  ref={cancelRef}
                  onClick={() => setIsConfirmOpen(false)}
                  disabled={reinitializing}
                  borderRadius="9px"
                >
                  Cancel
                </Button>
                <Button
                  colorScheme="red"
                  onClick={handleReinitialize}
                  ml={3}
                  isLoading={reinitializing}
                  borderRadius="9px"
                >
                  Reinitialize
                </Button>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialogOverlay>
        </AlertDialog>
      )}
    </Box>
  );
}
